/**
 * Fleet git-health page (fleet_git_health_orchestrator_2026_06_10.md Phase 2).
 *
 * One page answering "which worktrees anywhere in the fleet are dirty/behind/
 * diverged vs LDR, and are the reporter + FF-pull crons alive" across laptops +
 * VMs. Reads GET /api/fleet/git-health (the central fans out to every VM
 * server-side). Dead reporter / dead FF-pull cron is itself a visible red state.
 *
 * Operator decision v2 (2026-06-10) moves the PRIMARY operator view into
 * deployment-ui; this page stays on the orchestrator dashboard for worker-ops
 * use (it owns the slot/host state).
 */

import { useEffect, useState } from "react";

import { Panel } from "./components";
import type { FleetGitHealthResponse, FleetGitHealthSummary, RepoGitState, SlotGitHealth } from "./types";

// ── pure display mappers (vitest-covered) ──────────────────────────────────

const _RED = "var(--status-stale)";
const _AMBER = "var(--status-blocked)";
const _GREEN = "var(--status-working)";
const _MUTE = "var(--fg-2)";

/** GitHub rate-budget % remaining → colour token. <10% red, <25% amber, else green. */
export function rateBudgetTone(pct: number): string {
  if (pct < 10) return _RED;
  if (pct < 25) return _AMBER;
  return _GREEN;
}

/** Per-repo state → colour token. Drift states (ahead/diverged) are red. */
export function repoStateColor(state: RepoGitState, driftViolation: boolean): string {
  if (driftViolation) return _RED;
  switch (state) {
    case "clean":
      return _GREEN;
    case "behind":
      return _AMBER;
    case "dirty":
      return _AMBER;
    default:
      return _MUTE;
  }
}

export interface SlotBadge {
  label: string;
  color: string;
}

/**
 * Liveness/health badges for one slot, worst-first. A dead reporter or dead
 * FF-pull cron is a first-class red badge (never a silent gap); drift
 * violations roll up to a red badge; dirty/behind are amber.
 */
export function slotBadges(slot: SlotGitHealth): SlotBadge[] {
  const badges: SlotBadge[] = [];
  if (slot.reporter_stale) badges.push({ label: "reporter dead", color: _RED });
  if (slot.ff_cron_stale) badges.push({ label: "ff-pull dead", color: _RED });
  const drift = slot.repos.filter((r) => r.drift_violation).length;
  if (drift > 0) badges.push({ label: `${drift} drift`, color: _RED });
  const dirty = slot.repos.filter((r) => r.dirty_files > 0 || r.state === "dirty").length;
  if (dirty > 0) badges.push({ label: `${dirty} dirty`, color: _AMBER });
  const behind = slot.repos.filter((r) => r.behind > 0 || r.state === "behind").length;
  if (behind > 0) badges.push({ label: `${behind} behind`, color: _AMBER });
  if (badges.length === 0) badges.push({ label: "clean", color: _GREEN });
  return badges;
}

export interface SummaryChip {
  label: string;
  value: number;
  alert: boolean;
}

/** Header summary chips. Drift / stale-reporter / dead-ff-cron are alert chips. */
export function summaryChips(s: FleetGitHealthSummary): SummaryChip[] {
  return [
    { label: "hosts", value: s.hosts, alert: false },
    { label: "slots", value: s.slots, alert: false },
    { label: "repos", value: s.repos_total, alert: false },
    { label: "dirty", value: s.dirty, alert: s.dirty > 0 },
    { label: "behind", value: s.behind, alert: false },
    { label: "drift", value: s.drift_violations, alert: s.drift_violations > 0 },
    { label: "reporter dead", value: s.reporter_stale_slots, alert: s.reporter_stale_slots > 0 },
    { label: "ff-pull dead", value: s.ff_cron_stale_slots, alert: s.ff_cron_stale_slots > 0 },
  ];
}

// ── page ────────────────────────────────────────────────────────────────────

interface FleetGitProps {
  baseUrl: string;
  authToken: string | null;
  onBack?: () => void;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ok"; data: FleetGitHealthResponse }
  | { kind: "error"; message: string };

export function FleetGit({ baseUrl, authToken, onBack }: FleetGitProps) {
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    async function fetchHealth() {
      try {
        const res = await fetch(`${baseUrl}/api/fleet/git-health?scope=fleet`, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as FleetGitHealthResponse;
        if (!cancelled) setLoad({ kind: "ok", data });
      } catch (e) {
        if (!cancelled) setLoad({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      }
    }
    void fetchHealth();
    const t = setInterval(() => void fetchHealth(), 30_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [baseUrl, authToken]);

  return (
    <div style={{ padding: "16px 24px", maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        {onBack && (
          <button className="btn btn-sm" onClick={onBack}>
            ← Fleet
          </button>
        )}
        <h1 style={{ fontSize: 20, margin: 0 }}>Fleet Git-Health</h1>
        <div style={{ marginLeft: "auto" }}>
          <GhRateLimit baseUrl={baseUrl} authToken={authToken} />
        </div>
      </div>

      {load.kind === "loading" && <div style={{ color: _MUTE }}>Loading…</div>}
      {load.kind === "error" && (
        <div style={{ color: _RED }}>
          Failed to load: <span className="mono">{load.message}</span>
        </div>
      )}

      {load.kind === "ok" && <FleetBody data={load.data} />}
    </div>
  );
}

interface GhRatePool {
  limit: number;
  remaining: number;
  used: number;
  reset: number;
}
interface GhRateResponse {
  fetched_at?: string;
  alert_pct?: number;
  alerted?: string[];
  resources?: Record<string, GhRatePool>;
}

/** GitHub API rate-budget tracker. The whole fleet shares ONE PAT, so a low
 * `core` (REST) pool is the fleet-wide 403 source (it blinds CIReconcile). Polls
 * the cached, free `/api/gh-rate-limit` snapshot every 60s. */
function GhRateLimit({ baseUrl, authToken }: { baseUrl: string; authToken?: string | null }) {
  const [data, setData] = useState<GhRateResponse | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function fetchRate() {
      try {
        const res = await fetch(`${baseUrl}/api/gh-rate-limit`, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j = (await res.json()) as GhRateResponse;
        if (!cancelled) {
          setData(j);
          setErr(false);
        }
      } catch {
        if (!cancelled) setErr(true);
      }
    }
    void fetchRate();
    const t = setInterval(() => void fetchRate(), 60_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [baseUrl, authToken]);

  if (err) return <span style={{ fontSize: 12, color: _MUTE }}>GH rate: n/a</span>;
  if (!data?.resources) return <span style={{ fontSize: 12, color: _MUTE }}>GH rate…</span>;

  const pools: { key: string; label: string }[] = [
    { key: "core", label: "REST" },
    { key: "graphql", label: "GraphQL" },
  ];
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12 }}
      title="GitHub API budget (shared fleet PAT) · resets hourly"
    >
      <span style={{ color: _MUTE }}>GH budget:</span>
      {pools.map(({ key, label }) => {
        const p = data.resources?.[key];
        if (!p) return null;
        const pct = p.limit > 0 ? Math.floor((p.remaining * 100) / p.limit) : 0;
        const tone = rateBudgetTone(pct);
        return (
          <span key={key} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ color: _MUTE }}>{label}</span>
            <span
              style={{
                display: "inline-block",
                width: 46,
                height: 6,
                background: "var(--bg-2)",
                borderRadius: 3,
                overflow: "hidden",
              }}
            >
              <span style={{ display: "block", width: `${pct}%`, height: "100%", background: tone }} />
            </span>
            <span className="mono" style={{ color: tone }}>
              {p.remaining}/{p.limit}
            </span>
          </span>
        );
      })}
    </div>
  );
}

function FleetBody({ data }: { data: FleetGitHealthResponse }) {
  const chips = summaryChips(data.summary);
  return (
    <>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        {chips.map((c) => (
          <div
            key={c.label}
            style={{
              background: "var(--bg-1)",
              border: `1px solid ${c.alert ? _RED : "var(--bg-2)"}`,
              borderRadius: 6,
              padding: "6px 12px",
              fontSize: 12.5,
            }}
          >
            <span className="mono" style={{ color: c.alert ? _RED : "var(--fg-1)", fontWeight: 600 }}>
              {c.value}
            </span>{" "}
            <span style={{ color: _MUTE }}>{c.label}</span>
          </div>
        ))}
        {data.scope === "local" && (
          <div style={{ fontSize: 11, color: _MUTE, alignSelf: "center" }}>(local scope — fleet proxy off)</div>
        )}
      </div>

      {data.vm_errors.length > 0 && (
        <Panel title={`Unreachable VMs (${data.vm_errors.length})`} flush>
          <div style={{ padding: "8px 12px", fontSize: 12 }}>
            {data.vm_errors.map((e, i) => (
              <div key={i} style={{ color: _AMBER }}>
                <span className="mono">{e.vm_id}</span>: {e.error}
              </div>
            ))}
          </div>
        </Panel>
      )}

      {data.hosts.length === 0 && (
        <div style={{ color: _MUTE, fontSize: 13 }}>No slots have reported git-status yet.</div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 8 }}>
        {data.hosts.map((host) => (
          <Panel
            key={`${host.host}:${host.vm_id ?? ""}`}
            title={host.vm_id && host.vm_id !== host.host ? `${host.host} · ${host.vm_id}` : host.host}
            count={host.slots.length}
            flush
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              {host.slots
                .slice()
                .sort((a, b) => a.slot_id - b.slot_id)
                .map((slot) => (
                  <SlotRow key={slot.slot_id} slot={slot} />
                ))}
            </div>
          </Panel>
        ))}
      </div>
    </>
  );
}

function SlotRow({ slot }: { slot: SlotGitHealth }) {
  const [open, setOpen] = useState(false);
  const badges = slotBadges(slot);
  const interesting = slot.repos.filter((r) => r.state !== "clean" || r.drift_violation);
  return (
    <div style={{ borderBottom: "1px solid var(--bg-2)", padding: "8px 12px" }}>
      <div
        style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="mono" style={{ minWidth: 44, color: "var(--fg-1)" }}>
          slot {slot.slot_id}
        </span>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
          {badges.map((b, i) => (
            <span
              key={i}
              style={{
                fontSize: 11,
                color: b.color,
                border: `1px solid ${b.color}`,
                borderRadius: 4,
                padding: "1px 6px",
              }}
            >
              {b.label}
            </span>
          ))}
        </div>
        <span className="mono" style={{ fontSize: 10.5, color: _MUTE }}>
          {slot.ff_pull_last_result ? `ff:${slot.ff_pull_last_result}` : "ff:?"}
        </span>
        <span style={{ color: _MUTE, fontSize: 11 }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && (
        <div style={{ marginTop: 8, paddingLeft: 54 }}>
          {interesting.length === 0 && <div style={{ color: _MUTE, fontSize: 12 }}>all repos clean + up to date</div>}
          {interesting.map((r) => (
            <div key={r.name} style={{ display: "flex", gap: 10, fontSize: 12, padding: "2px 0" }}>
              <span className="mono" style={{ minWidth: 220, color: repoStateColor(r.state, r.drift_violation) }}>
                {r.name}
              </span>
              <span style={{ color: _MUTE }}>{r.state}</span>
              {r.dirty_files > 0 && <span style={{ color: _AMBER }}>{r.dirty_files} dirty</span>}
              {r.ahead > 0 && <span style={{ color: _RED }}>↑{r.ahead}</span>}
              {r.behind > 0 && <span style={{ color: _AMBER }}>↓{r.behind}</span>}
              {r.drift_violation && <span style={{ color: _RED, fontWeight: 600 }}>DRIFT</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
